import React from "react";
import {
	Button,
	Column,
	FormattedText,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	TruncatedText,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { Icon, IconContainer } from "../../../../Components/Icon";
import { Student, StudentMedicalContact, StudentNameFragment } from "../../../../Utilities";
import { StudentMedicalContactDeleteButton } from "../../../DeleteButtons";
import { StudentMedicalContactFormat } from "../../../Format";
import { StudentMedicalContactStudentForm } from "../../../Forms";
import { StudentMedicalContactDeletePermissionRequired } from "../PermissionRequired";
import styles from "./styles.module.css";

export type ModalStudentMedicalContact = Pick<
	StudentMedicalContact,
	"id" | "name" | "contactType" | "institutionName" | "phoneNumber" | "emailAddress" | "address" | "notes"
> & {
	student: Pick<Student, "id"> & StudentNameFragment;
};

export type StudentMedicalContactStudentModalProps = {
	studentMedicalContact: ModalStudentMedicalContact;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function StudentMedicalContactStudentModal({
	studentMedicalContact,
	ifRef,
	onClose,
	className,
	style,
}: StudentMedicalContactStudentModalProps) {
	const { isModalOpen: isEditModalOpen, openModal: openEditModal, closeModal: closeEditModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
				<Modal.Header>
					<Row justify="spaced-start" align="center">
						<IconContainer color="var(--theme-colors__blue--background)">
							<Icon.AddressBook color="var(--theme-colors__blue)" size={18} />
						</IconContainer>

						<Heading.H2 noMargin style={{ minWidth: 0 }}>
							<TruncatedText>
								{StudentMedicalContactFormat.Fields.contactType(studentMedicalContact.contactType)}
							</TruncatedText>
						</Heading.H2>
					</Row>
				</Modal.Header>
				<Modal.Body>
					<Column justify="spaced-start" verticalSpacing="1rem">
						<Column justify="spaced-start" verticalSpacing="0.25rem">
							<TruncatedText className={styles["name"]}>
								{StudentMedicalContactFormat.name(studentMedicalContact)}
							</TruncatedText>

							<span className={styles["title"]}>
								{StudentMedicalContactFormat.Fields.contactType(studentMedicalContact.contactType)}{" "}
								<If condition={!!studentMedicalContact.institutionName}>
									at {StudentMedicalContactFormat.Fields.institutionName(studentMedicalContact.institutionName)}
								</If>
							</span>

							<Button variant="link" size="small" onClick={openEditModal}>
								Edit
							</Button>
						</Column>

						<Column justify="spaced-start">
							<Column justify="spaced-start" verticalSpacing="0.25rem" className={styles["labeled-item"]}>
								<span className={styles["labeled-item__label"]}>Phone</span>
								<span className={styles["labeled-item__value"]}>
									{studentMedicalContact.phoneNumber
										? StudentMedicalContactFormat.Fields.phoneNumber(studentMedicalContact.phoneNumber)
										: "Not listed"}
								</span>
							</Column>

							<Column justify="spaced-start" verticalSpacing="0.25rem" className={styles["labeled-item"]}>
								<span className={styles["labeled-item__label"]}>Email</span>
								<span className={styles["labeled-item__value"]}>
									{studentMedicalContact.emailAddress
										? StudentMedicalContactFormat.Fields.emailAddress(studentMedicalContact.emailAddress)
										: "Not listed"}
								</span>
							</Column>

							<Column justify="spaced-start" verticalSpacing="0.25rem" className={styles["labeled-item"]}>
								<span className={styles["labeled-item__label"]}>Address</span>
								<span className={styles["labeled-item__value"]}>
									{studentMedicalContact.address
										? StudentMedicalContactFormat.Fields.address(studentMedicalContact.address)
										: "Not listed"}
								</span>
							</Column>
						</Column>

						<If condition={!!studentMedicalContact.notes}>
							<Column justify="spaced-start" verticalSpacing="0.25rem" className={styles["labeled-item"]}>
								<span className={styles["labeled-item__label"]}>Notes</span>
								<FormattedText className={styles["labeled-item__value"]}>{studentMedicalContact.notes}</FormattedText>
							</Column>
						</If>
					</Column>
				</Modal.Body>
			</Modal>

			<Modal.If condition={isEditModalOpen}>
				<EditModal studentMedicalContact={studentMedicalContact} onClose={closeEditModal} />
			</Modal.If>
		</React.Fragment>
	);
}

const editFormStateArgs = { editMode: EditMode.WriteOnly };

type EditModalProps = {
	studentMedicalContact: ModalStudentMedicalContact;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

function EditModal({ studentMedicalContact, ifRef, onClose, className, style }: EditModalProps) {
	const onSuccess = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["edit-modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Edit Medical Contact</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start">
					<StudentMedicalContactStudentForm.ControlledDetail
						studentMedicalContact={studentMedicalContact}
						formStateArgs={editFormStateArgs}
						noChangesDialog
						onSuccess={onSuccess}
					/>

					<StudentMedicalContactDeletePermissionRequired>
						<StudentMedicalContactDeleteButton
							instanceID={studentMedicalContact.id}
							size="small"
							onSuccess={onSuccess}
						/>
					</StudentMedicalContactDeletePermissionRequired>
				</Column>
			</Modal.Body>
		</Modal>
	);
}
